import { useEffect, useState } from 'react';
import { SectionStatusType } from '../data-qa-constants';
import { DataQaDataset, MmmConfig, SectionDiffs } from '../data-qa-types';
import { ContextVarsSection } from '../sections/ContextVarsSection';
import { StageSwitcher } from './StageSwitcher';

export interface ContextVarsStage {
  label: string;
  config: MmmConfig;
  dataset: DataQaDataset;
  diffs: SectionDiffs;
  status: SectionStatusType;
  notes: string;
  pending: boolean;
  isColdStart: boolean;
  onApprove: () => void;
  onFlag: (notes: string) => void;
  onNoteChange: (notes: string) => void;
}

interface ContextVarsStageTabsProps {
  stages: ContextVarsStage[];
  // For the Promo Flag's calendar of Offline Inputs promos.
  company: string;
  // Reports which stage is on screen -- DataQA.tsx's own "Data is identical across
  // models" banner sits above this component and is otherwise blind to which tab is
  // active, so it can't tell Total Business's own matches from GQV's. Fired on mount
  // (reporting the initial 0) and on every tab click.
  onActiveIndexChange?: (index: number) => void;
}

// Brand Equity's two modeling stages can genuinely declare different
// context_vars — confirmed on real `away` data that GQV's list is missing
// three outlier flags Total Business's has (Sean confirmed this is expected:
// the two stages are genuinely different models with good reason to encode
// different outliers, not a data-hygiene error). See DataQA.tsx, which only
// renders this wrapper when the two stages' contextVars section content hash
// actually differ. Each tab gets its own independent approve/flag, mirroring
// DependentVarStageTabs.
export const ContextVarsStageTabs = ({
  stages,
  company,
  onActiveIndexChange
}: ContextVarsStageTabsProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = stages[activeIndex];

  useEffect(() => {
    onActiveIndexChange?.(activeIndex);
  }, [activeIndex, onActiveIndexChange]);

  return (
    <div className='data-qa-stage-tabs'>
      <StageSwitcher
        prompt='This model has two stages — choose one to review its context variables:'
        stages={stages.map(stage => ({
          key: stage.config.configPath,
          label: stage.label,
          status: stage.status
        }))}
        activeIndex={activeIndex}
        onSelect={setActiveIndex}
      />
      <ContextVarsSection
        config={active.config}
        dataset={active.dataset}
        diffs={active.diffs}
        status={active.status}
        notes={active.notes}
        pending={active.pending}
        isColdStart={active.isColdStart}
        onApprove={active.onApprove}
        onFlag={active.onFlag}
        onNoteChange={active.onNoteChange}
        company={company}
      />
    </div>
  );
};
